import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { publicApi } from "../../api/http";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import PageLoader from "../../components/PageLoader/PageLoader";
import ChartShareModal from "../../components/ChartShareModal/ChartShareModal";
import { DEFAULT_PLAYER_IMAGE } from "../../components/DraftOdds/DraftOddsComponents";
import "./ArrivalRankings.css";

const STAGE_ORDER = ["M0", "M1", "M2", "M3", "M4", "M5"];
const STAGE_LABELS = {
    M0: "Draft year",
    M1: "+1 year",
    M2: "+2 years",
    M3: "+3 years",
    M4: "+4 years",
    M5: "+5 years",
};
const METRICS = {
    dy0: { key: "dy0", horizon: 0, short: "This season" },
    dy1: { key: "dy1", horizon: 1, short: "Next season" },
    dy2: { key: "dy2", horizon: 2, short: "+2 seasons" },
    dy3: { key: "dy3", horizon: 3, short: "+3 seasons" },
};
const VIEW_MODES = {
    rankings: "Pre-debut rankings",
    debuts: "Debuts by year",
    rounds: "Debuts by round",
};
const POSITION_GROUPS = {
    all: "All pos",
    F: "Forwards",
    D: "Defensemen",
    G: "Goalies",
};
const RANKINGS_SORT_DEFAULT = { sort: "dy0", order: "desc" };
const DEBUTS_SORT_DEFAULT = { sort: "years_to_debut", order: "asc" };
const RANKINGS_SORT_ASC = new Set([
    "player",
    "position",
    "blend_rank",
    "overall_pick",
    "drafted_team",
    "nhl_rights_team",
    "round_label",
    "years_to_debut",
]);
const FORWARD_POSITIONS = new Set(["C", "LW", "RW", "F", "W", "S"]);
const DEFENSE_POSITIONS = new Set(["D", "LD", "RD"]);
const GOALIE_POSITIONS = new Set(["G"]);

function parseDraftYearParam(value) {
    const year = Number(value);
    if (!Number.isFinite(year) || year < 2000 || year > 2040) return null;
    return Math.trunc(year);
}

function parseViewParam(value) {
    return VIEW_MODES[value] ? value : "rankings";
}

function parsePositionParam(value) {
    return POSITION_GROUPS[value] ? value : "all";
}

function parseRoundParam(value) {
    if (!value || value === "all") return "all";
    const normalized = String(value).trim().toUpperCase();
    if (/^R([1-7]|8\+)$/.test(normalized) || normalized === "UNKNOWN") {
        return normalized === "UNKNOWN" ? "Unknown" : normalized;
    }
    if (normalized === "UNKNOWN") return "Unknown";
    return "all";
}

function parseOffsetParam(value) {
    if (value == null || value === "" || value === "all") return "all";
    const offset = Number(value);
    if (!Number.isFinite(offset) || offset < 0 || offset > 20) return "all";
    return String(Math.trunc(offset));
}

function parseStageParam(value) {
    return STAGE_ORDER.includes(value) ? value : null;
}

function parseMetricParam(value) {
    return METRICS[value] ? value : "dy0";
}

function parseOrderParam(value, fallback = "desc") {
    return value === "asc" || value === "desc" ? value : fallback;
}

function buildArrivalShareQuery({
    layout,
    pos,
    round,
    offset,
    stage,
    metric,
}) {
    const params = new URLSearchParams();
    if (layout) params.set("layout", layout);
    if (pos && pos !== "all") params.set("pos", pos);
    if (round && round !== "all") params.set("round", round);
    if (offset && offset !== "all") params.set("offset", offset);
    if (stage) params.set("stage", stage);
    if (metric && metric !== "dy0") params.set("metric", metric);
    const query = params.toString();
    return query ? `?${query}` : "";
}

function arrivalShareVariant(viewMode) {
    if (viewMode === "debuts") return "debuts-year";
    if (viewMode === "rounds") return "debuts-round";
    return "predebut";
}

function positionGroup(position) {
    const normalized = String(position || "")
        .trim()
        .toUpperCase();
    if (!normalized) return null;
    if (FORWARD_POSITIONS.has(normalized)) return "F";
    if (DEFENSE_POSITIONS.has(normalized)) return "D";
    if (GOALIE_POSITIONS.has(normalized)) return "G";
    if (normalized.includes("G")) return "G";
    if (normalized.includes("D")) return "D";
    if (
        normalized.includes("C") ||
        normalized.includes("W") ||
        normalized.includes("F") ||
        normalized.includes("S")
    ) {
        return "F";
    }
    return null;
}

function matchesPositionFilter(position, filter) {
    if (filter === "all") return true;
    return positionGroup(position) === filter;
}

function nhlTeamLogoUrl(abbr) {
    if (!abbr || String(abbr).length > 4) return null;
    return `https://assets.nhle.com/logos/nhl/svg/${String(abbr).toUpperCase()}_dark.svg`;
}

function TeamAbbrCell({ abbr, teamId, logoUrl, emptyLabel = "—" }) {
    if (!abbr && !teamId) {
        return (
            <span className="arrival-rankings-team-cell arrival-rankings-team-cell--empty">
                {emptyLabel}
            </span>
        );
    }
    const label = abbr || "—";
    const src = logoUrl || nhlTeamLogoUrl(abbr);
    const content = (
        <>
            {src ? (
                <img src={src} alt="" className="arrival-rankings-team-logo" />
            ) : null}
            <span>{label}</span>
        </>
    );
    if (!teamId) {
        return <span className="arrival-rankings-team-cell">{content}</span>;
    }
    return (
        <Link to={`/team/${teamId}`} className="arrival-rankings-team-link">
            {content}
        </Link>
    );
}

function parseBoard(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return { rows: [], stages: [], available_years: [], debut_summary: null };
        }
    }
    return data && typeof data === "object"
        ? data
        : { rows: [], stages: [], available_years: [], debut_summary: null };
}

function seasonLabel(year) {
    if (year == null || Number.isNaN(Number(year))) return "—";
    const start = Number(year);
    return `${start}–${String(start + 1).slice(-2)}`;
}

function formatPct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${(Number(value) * 100).toFixed(1)}%`;
}

function formatCount(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return Number(value).toLocaleString();
}

function playerName(row) {
    return `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.player_id;
}

function compareNullableNumber(a, b, ascending) {
    const aMissing = a == null || Number.isNaN(Number(a));
    const bMissing = b == null || Number.isNaN(Number(b));
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const diff = Number(a) - Number(b);
    return ascending ? diff : -diff;
}

function compareNullableString(a, b, ascending) {
    const left = (a || "").toString();
    const right = (b || "").toString();
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    const diff = left.localeCompare(right);
    return ascending ? diff : -diff;
}

function emptyDebutSummary(draftYear) {
    return {
        draft_year: draftYear,
        drafted: 0,
        debuted: 0,
        still_pre_debut: 0,
        pct_debuted: 0,
        seasons_elapsed: 0,
        observed_offsets: 0,
        by_years: [],
        by_round: [],
        debuted_players: [],
    };
}

function ArrivalRankings() {
    const { draftYear, setDraftYear } = useContext(DraftYearContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [board, setBoard] = useState({
        rows: [],
        stages: [],
        available_years: [],
        debut_summary: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareLayout, setShareLayout] = useState("summary");

    const viewMode = parseViewParam(searchParams.get("view"));
    const positionFilter = parsePositionParam(searchParams.get("pos"));
    const roundFilter = parseRoundParam(searchParams.get("round"));
    const yearsToDebutFilter = parseOffsetParam(searchParams.get("offset"));
    const metric = parseMetricParam(searchParams.get("metric"));
    const stageFromUrl = parseStageParam(searchParams.get("stage"));
    const orderBy = searchParams.get("sort") || (
        viewMode === "rankings" ? RANKINGS_SORT_DEFAULT.sort : DEBUTS_SORT_DEFAULT.sort
    );
    const order = parseOrderParam(
        searchParams.get("order"),
        viewMode === "rankings" ? RANKINGS_SORT_DEFAULT.order : DEBUTS_SORT_DEFAULT.order
    );
    const debutOrderBy = searchParams.get("debut_sort") || DEBUTS_SORT_DEFAULT.sort;
    const debutOrder = parseOrderParam(
        searchParams.get("debut_order"),
        DEBUTS_SORT_DEFAULT.order
    );

    const patchParams = (updates, { replace = true } = {}) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                Object.entries(updates).forEach(([key, value]) => {
                    if (value == null || value === "") {
                        next.delete(key);
                        return;
                    }
                    next.set(key, String(value));
                });
                if (!next.get("year")) {
                    next.set("year", String(draftYear));
                }
                return next;
            },
            { replace }
        );
    };

    const yearSyncedRef = useRef(false);
    const yearParam = searchParams.get("year");

    // Prefer ?year= on first load so shared links win over localStorage.
    useLayoutEffect(() => {
        const fromUrl = parseDraftYearParam(yearParam);
        if (fromUrl != null && fromUrl !== draftYear) {
            setDraftYear(fromUrl);
        }
        yearSyncedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once
    }, []);

    // Browser back/forward on ?year=
    useEffect(() => {
        if (!yearSyncedRef.current) return;
        const fromUrl = parseDraftYearParam(yearParam);
        if (fromUrl != null && fromUrl !== draftYear) {
            setDraftYear(fromUrl);
        }
        // Only react to year query changes, not picker-driven draftYear updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yearParam, setDraftYear]);

    // Keep ?year= in sync with the draft-year picker.
    useEffect(() => {
        if (!yearSyncedRef.current) return;
        if (String(draftYear) === yearParam) return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("year", String(draftYear));
                return next;
            },
            { replace: true }
        );
    }, [draftYear, yearParam, setSearchParams]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        publicApi
            .get("/prospect_model_projections/board", { params: { draft_year: draftYear } })
            .then((response) => {
                if (cancelled) return;
                const next = parseBoard(response.data);
                setBoard(next);
                const stages = STAGE_ORDER.filter((kind) => next.stages.includes(kind));
                const current = next.rows.find((row) => row.is_dy0_stage)?.kind;
                const urlStage = parseStageParam(
                    new URLSearchParams(window.location.search).get("stage")
                );
                const preferred =
                    urlStage && stages.includes(urlStage)
                        ? urlStage
                        : current && stages.includes(current)
                          ? current
                          : stages[0] || null;
                if (preferred && preferred !== urlStage) {
                    setSearchParams(
                        (prev) => {
                            const params = new URLSearchParams(prev);
                            params.set("stage", preferred);
                            params.set("year", String(draftYear));
                            return params;
                        },
                        { replace: true }
                    );
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching arrival rankings:", err);
                setError("Failed to load NHL arrival rankings.");
                setBoard({
                    rows: [],
                    stages: [],
                    available_years: [],
                    debut_summary: emptyDebutSummary(draftYear),
                });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [draftYear, setSearchParams]);

    const debutSummary = board.debut_summary || emptyDebutSummary(draftYear);
    const maxDebutCount = Math.max(
        1,
        ...(debutSummary.by_years || []).map((row) => Number(row.debuts) || 0)
    );

    const stages = useMemo(
        () => STAGE_ORDER.filter((kind) => board.stages.includes(kind)),
        [board.stages]
    );

    const stage =
        stageFromUrl && stages.includes(stageFromUrl)
            ? stageFromUrl
            : stages[0] || null;

    const rounds = useMemo(() => {
        const fromSummary = (debutSummary.by_round || []).map((row) => row.round_label);
        if (fromSummary.length) return ["all", ...fromSummary];
        const set = new Set();
        board.rows.forEach((row) => {
            if (row.round_label) set.add(row.round_label);
        });
        return ["all", ...[...set].sort()];
    }, [board.rows, debutSummary.by_round]);

    const stageRows = useMemo(() => {
        return board.rows.filter((row) => {
            if (stage && row.kind !== stage) return false;
            // Rankings are for players who have not debuted yet.
            if (row.has_debuted) return false;
            if (!matchesPositionFilter(row.position, positionFilter)) return false;
            if (roundFilter !== "all" && row.round_label !== roundFilter) return false;
            return true;
        });
    }, [board.rows, stage, positionFilter, roundFilter]);

    const rankedRows = useMemo(() => {
        const withRank = [...stageRows]
            .sort((left, right) => {
                const byMetric = compareNullableNumber(left[metric], right[metric], false);
                if (byMetric !== 0) return byMetric;
                const byBlend = compareNullableNumber(left.blend_rank, right.blend_rank, true);
                if (byBlend !== 0) return byBlend;
                return playerName(left).localeCompare(playerName(right));
            })
            .map((row, index) => ({ ...row, viewRank: index + 1 }));

        return [...withRank].sort((left, right) => {
            if (orderBy === "player") {
                const diff = playerName(left).localeCompare(playerName(right));
                return order === "asc" ? diff : -diff;
            }
            if (
                orderBy === "position" ||
                orderBy === "drafted_team" ||
                orderBy === "nhl_rights_team" ||
                orderBy === "round_label"
            ) {
                return compareNullableString(left[orderBy], right[orderBy], order === "asc");
            }
            if (
                orderBy === "overall_pick" ||
                orderBy === "blend_rank" ||
                orderBy === "years_to_debut" ||
                orderBy === "nhl_games_played"
            ) {
                return compareNullableNumber(left[orderBy], right[orderBy], order === "asc");
            }
            return compareNullableNumber(left[orderBy], right[orderBy], order === "asc");
        });
    }, [stageRows, metric, orderBy, order]);

    const debutedPlayers = useMemo(() => {
        const fromSummary = debutSummary.debuted_players;
        let source;
        if (Array.isArray(fromSummary) && fromSummary.length > 0) {
            source = fromSummary;
        } else {
            const unique = new Map();
            board.rows.forEach((row) => {
                if (!row.has_debuted) return;
                if (!unique.has(row.player_id)) unique.set(row.player_id, row);
            });
            source = [...unique.values()];
        }
        return source
            .filter((row) => {
                if (!matchesPositionFilter(row.position, positionFilter)) return false;
                if (roundFilter !== "all" && row.round_label !== roundFilter) return false;
                if (
                    yearsToDebutFilter !== "all" &&
                    Number(row.years_to_debut) !== Number(yearsToDebutFilter)
                ) {
                    return false;
                }
                return true;
            })
            .sort((left, right) => {
                if (debutOrderBy === "nhl_games_played") {
                    const byGp = compareNullableNumber(
                        left.nhl_games_played,
                        right.nhl_games_played,
                        debutOrder === "asc"
                    );
                    if (byGp !== 0) return byGp;
                    return compareNullableNumber(left.overall_pick, right.overall_pick, true);
                }
                const byYears = compareNullableNumber(
                    left.years_to_debut,
                    right.years_to_debut,
                    true
                );
                if (byYears !== 0) return byYears;
                return compareNullableNumber(left.overall_pick, right.overall_pick, true);
            });
    }, [
        board.rows,
        debutSummary.debuted_players,
        positionFilter,
        roundFilter,
        yearsToDebutFilter,
        debutOrderBy,
        debutOrder,
    ]);

    const handleDebutSort = (column) => {
        let nextOrder = column === "nhl_games_played" ? "desc" : "asc";
        if (debutOrderBy === column) {
            nextOrder = debutOrder === "asc" ? "desc" : "asc";
        }
        const updates = {
            debut_sort: column,
            debut_order: nextOrder,
        };
        if (
            column === DEBUTS_SORT_DEFAULT.sort &&
            nextOrder === DEBUTS_SORT_DEFAULT.order
        ) {
            updates.debut_sort = null;
            updates.debut_order = null;
        }
        patchParams(updates);
    };

    const selectDebutYear = (yearsSinceDraft) => {
        const next =
            yearsToDebutFilter !== "all" &&
            Number(yearsToDebutFilter) === Number(yearsSinceDraft)
                ? null
                : String(yearsSinceDraft);
        patchParams({
            view: "debuts",
            offset: next,
        });
    };

    const selectDebutRound = (roundLabel) => {
        const next = roundFilter === roundLabel ? null : roundLabel;
        patchParams({
            view: "rounds",
            round: next,
            offset: null,
        });
    };

    const handleViewMode = (_event, next) => {
        if (!next) return;
        const updates = {
            view: next === "rankings" ? null : next,
        };
        if (next === "rankings" || next === "rounds") {
            updates.offset = null;
        }
        patchParams(updates);
    };

    const y0 =
        rankedRows[0]?.season_year_y0 ??
        (stage ? draftYear + Number(stage.slice(1)) : draftYear);

    const handleSort = (column) => {
        const updates = {};
        if (METRICS[column]) {
            updates.metric = column === "dy0" ? null : column;
        }
        let nextOrder = RANKINGS_SORT_ASC.has(column) ? "asc" : "desc";
        if (orderBy === column) {
            nextOrder = order === "asc" ? "desc" : "asc";
        }
        updates.sort = column;
        updates.order = nextOrder;
        if (
            column === RANKINGS_SORT_DEFAULT.sort &&
            nextOrder === RANKINGS_SORT_DEFAULT.order
        ) {
            updates.sort = null;
            updates.order = null;
        }
        patchParams(updates);
    };

    const handleMetric = (_event, next) => {
        if (!next) return;
        patchParams({
            metric: next === "dy0" ? null : next,
            sort: next === RANKINGS_SORT_DEFAULT.sort ? null : next,
            order: null,
        });
    };

    const handleStage = (_event, next) => {
        if (!next) return;
        patchParams({ stage: next });
    };

    const setPositionFilter = (next) => {
        patchParams({ pos: next === "all" ? null : next });
    };

    const setRoundFilter = (next) => {
        patchParams({ round: next === "all" ? null : next });
    };

    const shareVariant = arrivalShareVariant(viewMode);
    const effectiveShareLayout =
        shareVariant === "predebut" ? "players" : shareLayout;
    const shareQuery = buildArrivalShareQuery({
        layout: effectiveShareLayout,
        pos: positionFilter,
        round: roundFilter,
        offset: yearsToDebutFilter,
        stage,
        metric,
    });
    const shareMeta = {
        title:
            shareVariant === "predebut"
                ? `${draftYear} Draft · Pre-debut Top 5`
                : shareVariant === "debuts-year"
                  ? `${draftYear} Draft · Debuts by Year`
                  : `${draftYear} Draft · Debuts by Round`,
        description:
            shareVariant === "predebut"
                ? "Share the top pre-debut arrival odds for this filtered slice."
                : "Share a flashy breakdown or the debuted players from this slice.",
        fileName: `nhl-arrival-${draftYear}-${shareVariant}-${effectiveShareLayout}.png`,
        cardPath: `/share/arrival-rankings/${shareVariant}/${draftYear}/card${shareQuery}`,
        sharePath: `/share/arrival-rankings/${shareVariant}/${draftYear}${shareQuery}`,
        layoutOptions:
            shareVariant === "predebut"
                ? null
                : [
                      { id: "summary", label: "Breakdown" },
                      { id: "players", label: "Players" },
                  ],
    };

    const openShare = () => {
        setShareLayout(shareVariant === "predebut" ? "players" : "summary");
        setShareOpen(true);
    };

    const sortLabel = (column) => (
        <TableSortLabel
            active={orderBy === column}
            direction={
                orderBy === column
                    ? order
                    : [
                          "blend_rank",
                          "player",
                          "position",
                          "overall_pick",
                          "drafted_team",
                          "nhl_rights_team",
                          "round_label",
                          "years_to_debut",
                      ].includes(column)
                    ? "asc"
                    : "desc"
            }
            onClick={() => handleSort(column)}
            className="arrival-rankings-sort"
        />
    );

    const debutedSliceLabel = [
        yearsToDebutFilter !== "all"
            ? Number(yearsToDebutFilter) === 0
                ? "draft year"
                : `+${yearsToDebutFilter}`
            : null,
        roundFilter !== "all" ? roundFilter : null,
        positionFilter !== "all"
            ? POSITION_GROUPS[positionFilter] || positionFilter
            : null,
    ]
        .filter(Boolean)
        .map((part) => ` · ${part}`)
        .join("");

    const debutedPlayersPanel =
        debutedPlayers.length > 0 ? (
            <>
                <Typography className="arrival-rankings-count">
                    {debutedPlayers.length} debuted players{debutedSliceLabel}
                </Typography>
                <TableContainer className="arrival-rankings-table-wrap">
                    <Table className="arrival-rankings-table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Player</TableCell>
                                <TableCell>Pos</TableCell>
                                <TableCell>Drafted</TableCell>
                                <TableCell>Rights</TableCell>
                                <TableCell>Round</TableCell>
                                <TableCell align="right">Overall</TableCell>
                                <TableCell align="right">Years to debut</TableCell>
                                <TableCell>Debut season</TableCell>
                                <TableCell align="right">
                                    NHL GP{" "}
                                    <TableSortLabel
                                        active={debutOrderBy === "nhl_games_played"}
                                        direction={
                                            debutOrderBy === "nhl_games_played"
                                                ? debutOrder
                                                : "desc"
                                        }
                                        onClick={() => handleDebutSort("nhl_games_played")}
                                        className="arrival-rankings-sort"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {debutedPlayers.map((row) => (
                                <TableRow key={row.player_id}>
                                    <TableCell>
                                        <Link
                                            to={`/player/${row.player_id}?view=post-draft`}
                                            className="arrival-rankings-player"
                                        >
                                            <img
                                                src={
                                                    row.player_image_url || DEFAULT_PLAYER_IMAGE
                                                }
                                                alt=""
                                                className="arrival-rankings-player-img"
                                            />
                                            <span>{playerName(row)}</span>
                                        </Link>
                                    </TableCell>
                                    <TableCell>{row.position || "—"}</TableCell>
                                    <TableCell>
                                        <TeamAbbrCell
                                            abbr={row.drafted_team}
                                            teamId={row.drafted_team_id}
                                            logoUrl={row.drafted_team_logo_url}
                                        />
                                    </TableCell>
                                                <TableCell>
                                                    <TeamAbbrCell
                                                        abbr={row.nhl_rights_team}
                                                        teamId={row.nhl_rights_team_id}
                                                        logoUrl={row.nhl_rights_team_logo_url}
                                                        emptyLabel="No NHL rights"
                                                    />
                                                </TableCell>
                                    <TableCell>{row.round_label || "—"}</TableCell>
                                    <TableCell align="right">
                                        {row.overall_pick != null ? row.overall_pick : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.years_to_debut != null
                                            ? `+${row.years_to_debut}`
                                            : "—"}
                                    </TableCell>
                                    <TableCell>
                                        {seasonLabel(row.debut_season_year)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCount(row.nhl_games_played)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        ) : (
            <Typography className="arrival-rankings-empty">
                No debuted players in this slice yet.
            </Typography>
        );

    return (
        <>
            <Helmet>
                <title>{`${draftYear} Draft Arrival Rankings - Hockey Pipeline`}</title>
                <meta
                    name="description"
                    content={`Pre-debut NHL arrival odds for the ${draftYear} draft class, plus who has already debuted by year and round.`}
                />
                <link rel="canonical" href="https://hockeypipeline.com/arrival-rankings" />
            </Helmet>
            <PageLoader loading={loading} />
            <Container maxWidth="xl" className="arrival-rankings-page">
                <section className="home-panel arrival-rankings-hero">
                    <div className="arrival-rankings-hero-copy">
                        <Typography
                            variant="h4"
                            component="h1"
                            className="arrival-rankings-page-title"
                        >
                            {draftYear} Draft · Arrival Rankings
                        </Typography>
                        <Typography className="arrival-rankings-intro">
                            Pre-debut odds for players still waiting on an NHL game, plus who from
                            this class has already arrived.{" "}
                            <Link
                                to="/how-projections-work"
                                className="arrival-rankings-method-link"
                            >
                                How projections work
                            </Link>
                        </Typography>
                    </div>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ShareOutlinedIcon />}
                        className="arrival-rankings-share-btn"
                        onClick={openShare}
                    >
                        Share
                    </Button>
                </section>

                <section className="home-panel arrival-rankings-panel">
                    <div className="arrival-rankings-controls-grid">
                        <div className="arrival-rankings-controls">
                            <div className="arrival-rankings-control-row">
                                <div className="arrival-rankings-control">
                                    <Typography className="arrival-rankings-control-label">
                                        View
                                    </Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        value={viewMode}
                                        onChange={handleViewMode}
                                        size="small"
                                        className="arrival-rankings-toggles"
                                        aria-label="Arrival page view"
                                    >
                                        {Object.entries(VIEW_MODES).map(([value, label]) => (
                                            <ToggleButton key={value} value={value}>
                                                {label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </div>
                                <div className="arrival-rankings-control">
                                    <Typography className="arrival-rankings-control-label">
                                        Slice
                                    </Typography>
                                    <div className="arrival-rankings-slice-row">
                                        <ToggleButtonGroup
                                            exclusive
                                            value={positionFilter}
                                            onChange={(_event, next) =>
                                                next && setPositionFilter(next)
                                            }
                                            size="small"
                                            className="arrival-rankings-toggles"
                                            aria-label="Position filter"
                                        >
                                            {Object.entries(POSITION_GROUPS).map(
                                                ([value, label]) => (
                                                    <ToggleButton key={value} value={value}>
                                                        {label}
                                                    </ToggleButton>
                                                )
                                            )}
                                        </ToggleButtonGroup>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={roundFilter}
                                            onChange={(_event, next) =>
                                                next && setRoundFilter(next)
                                            }
                                            size="small"
                                            className="arrival-rankings-toggles"
                                            aria-label="Draft round filter"
                                        >
                                            {rounds.map((value) => (
                                                <ToggleButton key={value} value={value}>
                                                    {value === "all" ? "All rounds" : value}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            </div>

                            {viewMode === "rankings" && stages.length > 0 && (
                                <div className="arrival-rankings-control-row">
                                    <div className="arrival-rankings-control">
                                        <Typography className="arrival-rankings-control-label">
                                            Model
                                        </Typography>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={stage}
                                            onChange={handleStage}
                                            size="small"
                                            className="arrival-rankings-toggles"
                                            aria-label="Draft-stage model"
                                        >
                                            {stages.map((kind) => (
                                                <ToggleButton key={kind} value={kind}>
                                                    {kind}
                                                    <span className="arrival-rankings-toggle-hint">
                                                        {STAGE_LABELS[kind]}
                                                    </span>
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </div>
                                    <div className="arrival-rankings-control">
                                        <Typography className="arrival-rankings-control-label">
                                            Rank by
                                        </Typography>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={metric}
                                            onChange={handleMetric}
                                            size="small"
                                            className="arrival-rankings-toggles"
                                            aria-label="Season probability"
                                        >
                                            {Object.values(METRICS).map((item) => (
                                                <ToggleButton key={item.key} value={item.key}>
                                                    {item.horizon == null
                                                        ? item.short
                                                        : seasonLabel(y0 + item.horizon)}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="arrival-rankings-debut-panel">
                            <div className="arrival-rankings-debut-header">
                                <div>
                                    <Typography className="arrival-rankings-control-label">
                                        Class debuts
                                    </Typography>
                                    <Typography className="arrival-rankings-debut-title">
                                        {formatCount(debutSummary.debuted)} of{" "}
                                        {formatCount(debutSummary.drafted)} have debuted
                                    </Typography>
                                </div>
                                <div className="arrival-rankings-debut-stats">
                                    <div className="arrival-rankings-debut-stat">
                                        <span>
                                            {formatPct((debutSummary.pct_debuted || 0) / 100)}
                                        </span>
                                        <small>of class</small>
                                    </div>
                                    <div className="arrival-rankings-debut-stat">
                                        <span>
                                            {formatCount(debutSummary.still_pre_debut)}
                                        </span>
                                        <small>waiting</small>
                                    </div>
                                </div>
                            </div>

                            {(debutSummary.by_years || []).length > 0 ? (
                                <div className="arrival-rankings-debut-bars">
                                    {debutSummary.by_years.map((row) => {
                                        const isActive =
                                            yearsToDebutFilter !== "all" &&
                                            Number(yearsToDebutFilter) ===
                                                Number(row.years_since_draft);
                                        return (
                                            <button
                                                key={row.years_since_draft}
                                                type="button"
                                                className={`arrival-rankings-debut-bar${
                                                    isActive
                                                        ? " arrival-rankings-debut-bar--active"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    selectDebutYear(row.years_since_draft)
                                                }
                                                aria-pressed={isActive}
                                                title={`Show players who debuted in ${row.label}`}
                                            >
                                                <div className="arrival-rankings-debut-bar-track">
                                                    <div
                                                        className="arrival-rankings-debut-bar-fill"
                                                        style={{
                                                            height: `${Math.max(
                                                                8,
                                                                (Number(row.debuts) /
                                                                    maxDebutCount) *
                                                                    100
                                                            )}%`,
                                                        }}
                                                    />
                                                </div>
                                                <strong>{formatCount(row.debuts)}</strong>
                                                <span>{row.label}</span>
                                                <small>{seasonLabel(row.season_year)}</small>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <Typography className="arrival-rankings-debut-empty">
                                    No NHL debuts recorded for this class yet.
                                </Typography>
                            )}
                        </div>
                    </div>
                </section>

                {error && (
                    <Typography className="arrival-rankings-empty">{error}</Typography>
                )}

                {!loading && !error && viewMode === "rankings" && rankedRows.length === 0 && (
                    <Typography className="arrival-rankings-empty">
                        No pre-debut arrival projections for this slice of the {draftYear} draft
                        class.
                        {board.available_years?.length
                            ? ` Rankings are available for ${board.available_years.join(", ")}.`
                            : ""}
                    </Typography>
                )}

                {viewMode === "rankings" && rankedRows.length > 0 && (
                    <>
                        <Typography className="arrival-rankings-count">
                            {rankedRows.length} pre-debut players · {stage} · ranked by{" "}
                            {seasonLabel(y0 + (METRICS[metric]?.horizon || 0))}
                            {roundFilter !== "all" ? ` · ${roundFilter}` : ""}
                            {positionFilter !== "all"
                                ? ` · ${POSITION_GROUPS[positionFilter] || positionFilter}`
                                : ""}
                            {board.model_version ? ` · ${board.model_version}` : ""}
                        </Typography>
                        <TableContainer className="arrival-rankings-table-wrap">
                            <Table className="arrival-rankings-table" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--rank">
                                            #
                                        </TableCell>
                                        <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--player">
                                            Player {sortLabel("player")}
                                        </TableCell>
                                        <TableCell>Pos {sortLabel("position")}</TableCell>
                                        <TableCell>Drafted {sortLabel("drafted_team")}</TableCell>
                                        <TableCell align="right">
                                            Overall {sortLabel("overall_pick")}
                                        </TableCell>
                                        <TableCell>
                                            Rights {sortLabel("nhl_rights_team")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0)} % {sortLabel("dy0")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 1)} % {sortLabel("dy1")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 2)} % {sortLabel("dy2")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 3)} % {sortLabel("dy3")}
                                        </TableCell>
                                        <TableCell align="right">
                                            Class rank {sortLabel("blend_rank")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rankedRows.map((row) => (
                                        <TableRow
                                            key={`${row.player_id}-${row.kind}`}
                                            className="arrival-rankings-row"
                                        >
                                            <TableCell className="arrival-rankings-rank arrival-rankings-sticky arrival-rankings-sticky--rank">
                                                {row.viewRank}
                                            </TableCell>
                                            <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--player">
                                                <Link
                                                    to={`/player/${row.player_id}?view=post-draft`}
                                                    className="arrival-rankings-player"
                                                >
                                                    <img
                                                        src={
                                                            row.player_image_url ||
                                                            DEFAULT_PLAYER_IMAGE
                                                        }
                                                        alt=""
                                                        className="arrival-rankings-player-img"
                                                    />
                                                    <span>{playerName(row)}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell>{row.position || "—"}</TableCell>
                                            <TableCell className="arrival-rankings-team">
                                                <TeamAbbrCell
                                                    abbr={row.drafted_team}
                                                    teamId={row.drafted_team_id}
                                                    logoUrl={row.drafted_team_logo_url}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.overall_pick != null ? row.overall_pick : "—"}
                                            </TableCell>
                                            <TableCell className="arrival-rankings-team">
                                                <TeamAbbrCell
                                                    abbr={row.nhl_rights_team}
                                                    teamId={row.nhl_rights_team_id}
                                                    logoUrl={row.nhl_rights_team_logo_url}
                                                    emptyLabel="No NHL rights"
                                                />
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    metric === "dy0"
                                                        ? "arrival-rankings-sorted"
                                                        : undefined
                                                }
                                            >
                                                {formatPct(row.dy0)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    metric === "dy1"
                                                        ? "arrival-rankings-sorted"
                                                        : undefined
                                                }
                                            >
                                                {formatPct(row.dy1)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    metric === "dy2"
                                                        ? "arrival-rankings-sorted"
                                                        : undefined
                                                }
                                            >
                                                {formatPct(row.dy2)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    metric === "dy3"
                                                        ? "arrival-rankings-sorted"
                                                        : undefined
                                                }
                                            >
                                                {formatPct(row.dy3)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.blend_rank != null
                                                    ? `#${row.blend_rank}`
                                                    : "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {viewMode === "debuts" && (
                    <section className="home-panel arrival-rankings-view-panel">
                        <div className="arrival-rankings-view-header">
                            <Typography className="arrival-rankings-view-title">
                                Debuted players by years since draft
                            </Typography>
                            <Typography className="arrival-rankings-view-subtitle">
                                Players from the {draftYear} class who have already played an NHL
                                regular-season game, grouped by how long after the draft they
                                debuted. Position and round slices still apply.
                            </Typography>
                        </div>
                        <div className="arrival-rankings-debut-grid">
                            {(debutSummary.by_years || []).map((row) => {
                                const isActive =
                                    yearsToDebutFilter !== "all" &&
                                    Number(yearsToDebutFilter) ===
                                        Number(row.years_since_draft);
                                return (
                                    <button
                                        key={row.years_since_draft}
                                        type="button"
                                        className={`arrival-rankings-debut-card${
                                            isActive
                                                ? " arrival-rankings-debut-card--active"
                                                : ""
                                        }`}
                                        onClick={() => selectDebutYear(row.years_since_draft)}
                                        aria-pressed={isActive}
                                    >
                                        <Typography className="arrival-rankings-debut-card-label">
                                            {row.label}
                                        </Typography>
                                        <Typography className="arrival-rankings-debut-card-value">
                                            {formatCount(row.debuts)}
                                        </Typography>
                                        <Typography className="arrival-rankings-debut-card-meta">
                                            {seasonLabel(row.season_year)}
                                        </Typography>
                                    </button>
                                );
                            })}
                        </div>
                        {debutedPlayersPanel}
                    </section>
                )}

                {viewMode === "rounds" && (
                    <section className="home-panel arrival-rankings-view-panel">
                        <div className="arrival-rankings-view-header">
                            <Typography className="arrival-rankings-view-title">
                                Debuted players by draft round
                            </Typography>
                            <Typography className="arrival-rankings-view-subtitle">
                                Share of each {draftYear} round that has reached the NHL, and the
                                debuted players from the selected round. Position slice still
                                applies.
                            </Typography>
                        </div>
                        {(debutSummary.by_round || []).length > 0 ? (
                            <div className="arrival-rankings-round-grid">
                                {debutSummary.by_round.map((row) => (
                                    <button
                                        key={row.round_label}
                                        type="button"
                                        className={`arrival-rankings-round-card${
                                            roundFilter === row.round_label
                                                ? " arrival-rankings-round-card--active"
                                                : ""
                                        }`}
                                        onClick={() => selectDebutRound(row.round_label)}
                                    >
                                        <Typography className="arrival-rankings-round-label">
                                            {row.round_label}
                                        </Typography>
                                        <Typography className="arrival-rankings-round-value">
                                            {formatPct((row.pct_debuted || 0) / 100)}
                                        </Typography>
                                        <Typography className="arrival-rankings-round-meta">
                                            {formatCount(row.debuted)} / {formatCount(row.drafted)}{" "}
                                            debuted
                                        </Typography>
                                        <div className="arrival-rankings-round-meter">
                                            <div
                                                className="arrival-rankings-round-meter-fill"
                                                style={{
                                                    width: `${Math.min(
                                                        100,
                                                        Number(row.pct_debuted) || 0
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <Typography className="arrival-rankings-empty">
                                No round-level debut data for this class yet.
                            </Typography>
                        )}
                        <div className="arrival-rankings-round-players">
                            {debutedPlayersPanel}
                        </div>
                    </section>
                )}
            </Container>
            <ChartShareModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                cardPath={shareMeta.cardPath}
                sharePath={shareMeta.sharePath}
                title={shareMeta.title}
                description={shareMeta.description}
                fileName={shareMeta.fileName}
                layoutOptions={shareMeta.layoutOptions}
                layout={effectiveShareLayout}
                onLayoutChange={setShareLayout}
            />
        </>
    );
}

export default ArrivalRankings;
